import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $generateNodesFromDOM } from '@lexical/html';
import { $getRoot, $isParagraphNode, type LexicalNode } from 'lexical';
import {
  $createAiDraftSectionNode,
  $isAiDraftSectionNode,
  AiDraftSectionNode,
} from '@/components/LexicalEditor/nodes/AiDraftSectionNode';
import { $wrapTopLevelInlines } from '@/components/LexicalEditor/utils/wrapTopLevelInlines';

export type AiDraftSectionApi = {
  /** 같은 contractId 섹션이 있으면 그 자리에서 교체, 없으면 본문 끝에 추가 */
  upsert: (contractId: number, innerHtml: string) => void;
  remove: (contractId: number) => void;
  has: (contractId: number) => boolean;
};

type AiDraftSectionPluginProps = {
  /** mount 때 API, unmount 때 null */
  onApiChange: (api: AiDraftSectionApi | null) => void;
};

const $findSection = (contractId: number): AiDraftSectionNode | undefined =>
  $getRoot()
    .getChildren()
    .find(
      (node): node is AiDraftSectionNode =>
        $isAiDraftSectionNode(node) &&
        node.getContractId() === String(contractId),
    );

/** 빈 에디터의 초기 문단 하나 — 그 자리에 섹션을 넣어 본문 맨 위 빈 줄이 남지 않게 함 */
const $onlyEmptyParagraph = (): LexicalNode | undefined => {
  const children = $getRoot().getChildren();
  const [first] = children;
  return children.length === 1 &&
    $isParagraphNode(first) &&
    first.getChildrenSize() === 0
    ? first
    : undefined;
};

/**
 * 계약 품의 AI 초안 섹션을 노드 API 로 넣고 빼는 플러그인. innerHTML 직접 조작 없이
 * $generateNodesFromDOM → 섹션 노드 자식으로 붙임. 선택 영역은 건드리지 않아 커서 유지
 */
const AiDraftSectionPlugin = ({ onApiChange }: AiDraftSectionPluginProps) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const api: AiDraftSectionApi = {
      upsert: (contractId, innerHtml) => {
        editor.update(() => {
          const doc = new DOMParser().parseFromString(innerHtml, 'text/html');
          const children = $wrapTopLevelInlines(
            $generateNodesFromDOM(editor, doc),
          );
          const section = $createAiDraftSectionNode(String(contractId));
          section.append(...children);
          const target = $findSection(contractId) ?? $onlyEmptyParagraph();
          if (target) {
            target.replace(section);
          } else {
            $getRoot().append(section);
          }
        });
      },
      remove: (contractId) => {
        editor.update(() => {
          $findSection(contractId)?.remove();
        });
      },
      has: (contractId) =>
        editor.getEditorState().read(() => Boolean($findSection(contractId))),
    };
    onApiChange(api);
    return () => onApiChange(null);
  }, [editor, onApiChange]);

  return null;
};

export default AiDraftSectionPlugin;
